"use client";

import React, { useRef, useState } from "react";
import Link from "next/link";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ProblemSetEditor } from "~/components/ui/problem-set-editor";
import { createMistakeReview } from "./api";
import type { MistakeReviewSource } from "./ProblemSetActions";

type Props = {
  source: MistakeReviewSource;
  onClose: () => void;
  onCreated?: () => void;
};

export default function MistakeReviewDialog({
  source,
  onClose,
  onCreated,
}: Props) {
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const [pending, setPending] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<string>();
  const [result, setResult] =
    useState<Awaited<ReturnType<typeof createMistakeReview>>>();

  const submit = async (
    input: Parameters<
      React.ComponentProps<typeof ProblemSetEditor>["onSubmit"]
    >[0],
  ) => {
    if (submitting.current) return;
    const title = input.title.trim();
    if (!title) {
      setError("Введите название набора задач.");
      return;
    }
    submitting.current = true;
    setPending(true);
    setError(undefined);
    let succeeded = false;
    try {
      setResult(
        await createMistakeReview(source.problemSetId, {
          title,
          visibility: input.visibility,
        }),
      );
      succeeded = true;
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Не удалось создать набор.",
      );
    } finally {
      submitting.current = false;
      setPending(false);
    }
    if (succeeded) onCreated?.();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submitting.current) onClose();
      }}
    >
      <DialogContent
        ref={setContent}
        showCloseButton={!pending}
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Работа над ошибками</DialogTitle>
          <DialogDescription>
            Новый набор из завершённых задач с ошибочными ходами.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-4">
            <p>{result.name}</p>
            <p>Задач: {result.generatedTaskCount}</p>
            {result.skippedUnusableSourceCount > 0 && (
              <p role="alert">
                Пропущено исходных задач: {result.skippedUnusableSourceCount}.
                Не удалось надёжно восстановить ошибочные ходы.
              </p>
            )}
            <Link
              className="inline-block rounded border px-4 py-2"
              href={`/problem-sets/${result.problemSetId}/problems/${result.firstProblemId}${result.visibility === "hidden" ? "?showHidden=true" : ""}`}
            >
              Открыть набор
            </Link>
          </div>
        ) : (
          <>
            <ProblemSetEditor
              problemSetTitle={`${source.name} - работа над ошибками`}
              visibility={source.visibility}
              onSubmit={(input) => void submit(input)}
              busy={pending}
              showValidationErrors
              visibilityPortalContainer={content}
            />
            {error && <p role="alert">{error}</p>}
            {pending && <p role="status">Создание набора…</p>}
          </>
        )}
        <Button variant="outline" disabled={pending} onClick={onClose}>
          Закрыть
        </Button>
      </DialogContent>
    </Dialog>
  );
}
